import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih, tokens } from '../../lib/api';

/**
 * SİTE YÖNETİMİ — ekranların ortak parçaları. Yapı Shopify'ın "Online Store"
 * düzenini izler: sayfa başlığı + sağında birincil eylem, kartlar, dizin tabloları,
 * iki sütunlu düzenleme, kaydedilmemiş değişiklik çubuğu ve tek "Yayınla" penceresi.
 *
 * Buradaki her kaydetme TASLAKTIR; siteye ancak "Yayınla" ile çıkar.
 * İki dilli alanlar yan yana iki kutu DEĞİLDİR: ekrandaki tek DİL anahtarı hangi
 * dilin düzenlendiğini belirler (bkz. DilAnahtari, DilAlan).
 */
export type IkiDil = { tr: string; en: string };
export type Dil = 'tr' | 'en';
export type Degisiklik = { bolum: string; alan: string; once?: string; sonra?: string };
export type SiteDurum = {
  hatKurulu: boolean; eksikAyar: string[]; siteAdresi: string | null;
  calisan: { id: number; tur: 'YAYIN' | 'ONIZLEME' | 'GERI_AL'; baslangic: string; kullanici: string } | null;
  sonYayin: { id: number; tur: string; bitis: string | null; kullanici: string | null } | null;
  degisiklik: { var: boolean; ozet: Degisiklik[] };
  icerikVar: boolean;
  secenekler: { sosyal: string[]; segmentler: string[] };
};
export type Medya = {
  id: number; ad: string; yol: string; dosyaAdi: string; mime: string; boyut: number; alt: string;
  en: number | null; boy: number | null; eklendi: string; kullanim: string[];
};

export const IS_AD: Record<string, string> = { YAYIN: 'Yayın', ONIZLEME: 'Önizleme derlemesi', GERI_AL: 'Geri alma' };
export const GORSEL_KABUL = 'image/jpeg,image/png,image/webp';
export const bosIkiDil = (): IkiDil => ({ tr: '', en: '' });
export const boyutYaz = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Jeton taşıyan fetch (dosya yükleme ve görsel okuma için; `api` yalnız JSON döner). */
export async function yetkiliFetch(yol: string, init: RequestInit = {}): Promise<Response> {
  const cagir = () => fetch(yol, { ...init, headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {} });
  let res = await cagir();
  if (res.status === 401 && tokens.refresh) {
    await api('/api/me').catch(() => undefined);
    res = await cagir();
  }
  return res;
}

/* ─── Site durumu ──────────────────────────────────────────────────────── */
let siteAdresi: string | null = null;
/** Sitedeki görsellerin adresi durumla gelir; ilk durum okunana kadar görseller bekler. */
let adresGeldi: () => void = () => undefined;
const adresBekle = new Promise<void>((coz) => { adresGeldi = coz; });

/** Yayın durumu. Bir iş sürerken 2 saniyede bir yenilenir. */
export function useSiteDurum() {
  const [durum, setDurum] = useState<SiteDurum | null>(null);
  const yenile = useCallback(
    () => api<SiteDurum>('/api/site/durum').then((d) => { siteAdresi = d.siteAdresi; adresGeldi(); setDurum(d); return d; }).catch(() => null),
    [],
  );
  useEffect(() => { void yenile(); }, [yenile]);
  const suruyor = Boolean(durum?.calisan);
  useEffect(() => {
    if (!suruyor) return;
    const t = window.setInterval(() => void yenile(), 2000);
    return () => window.clearInterval(t);
  }, [suruyor, yenile]);
  return { durum, yenile };
}

/** Yayın durumu, tek satır: "Yayında" · "Yayınlanmamış N değişiklik" · "Yayın sürüyor". */
export function yayinOzeti(d: SiteDurum): { metin: string; tur: 'guncel' | 'bekliyor' | 'suruyor' } {
  if (d.calisan) return { metin: `${IS_AD[d.calisan.tur]} sürüyor`, tur: 'suruyor' };
  if (!d.sonYayin) return { metin: 'Henüz yayınlanmadı', tur: 'bekliyor' };
  if (d.degisiklik.var) return { metin: `Yayınlanmamış ${d.degisiklik.ozet.length} değişiklik`, tur: 'bekliyor' };
  return { metin: 'Yayında', tur: 'guncel' };
}

export function YayinRozeti({ durum }: { durum: SiteDurum | null }) {
  if (!durum) return null;
  const o = yayinOzeti(durum);
  return (
    <span className={`ccd-badge${o.tur === 'guncel' ? ' ccd-badge-success' : ' ccd-badge-warning'}`} data-site-rozet={o.tur}>
      <span className="ccd-badge-dot" />{o.metin}
    </span>
  );
}

/** Sayfa başlığı: solda (geri bağlantısı +) başlık + durum rozeti, sağda eylemler. */
export function SayfaBaslik({ baslik, geri, durum, alt, children }: {
  baslik: string; geri?: { to: string; ad: string }; durum?: SiteDurum | null; alt?: React.ReactNode; children?: React.ReactNode;
}) {
  return (
    <div className="ccd-site-bas">
      <div className="ccd-site-bas-sol">
        {geri && <Link className="ccd-site-bas-geri" to={geri.to} aria-label={geri.ad} title={geri.ad}>←</Link>}
        <div style={{ minWidth: 0 }}>
          <div className="ccd-site-bas-satir">
            <h1 className="ccd-title ccd-truncate">{baslik}</h1>
            {durum !== undefined && <YayinRozeti durum={durum} />}
          </div>
          {alt && <div className="ccd-meta ccd-truncate">{alt}</div>}
        </div>
      </div>
      {children && <div className="ccd-site-bas-eylem">{children}</div>}
    </div>
  );
}

/** Kart: başlık (+ sağında küçük eylem) ve içerik. */
export function Kart({ baslik, eylem, children, className }: { baslik?: string; eylem?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={`ccd-card ccd-stack ccd-site-kartim${className ? ` ${className}` : ''}`}>
      {(baslik || eylem) && (
        <div className="ccd-row-between">
          {baslik && <h2 className="ccd-site-kart-baslik">{baslik}</h2>}
          {eylem}
        </div>
      )}
      {children}
    </section>
  );
}

/** Kaydetme sonucu: yeşil bilgi ya da kırmızı hata. */
export function Sonuc({ bilgi, hata }: { bilgi: string | null; hata: string | null }) {
  return (
    <>
      {bilgi && <div className="ccd-site-not" role="status">{bilgi}</div>}
      {hata && <div className="ccd-site-not ccd-site-not-hata" role="alert">{hata}</div>}
    </>
  );
}

/* ─── Seçmeli anahtar (dil, görünüm, durum) ────────────────────────────── */
export function Secmeli<T extends string>({ etiket, secenekler, deger, degis, tam }: {
  etiket: string; secenekler: readonly (readonly [T, string])[]; deger: T; degis: (v: T) => void; tam?: boolean;
}) {
  return (
    <div className={`ccd-site-secmeli${tam ? ' ccd-site-secmeli-tam' : ''}`} role="group" aria-label={etiket}>
      {secenekler.map(([id, ad]) => (
        <button key={id} type="button" aria-pressed={deger === id} onClick={() => degis(id)}>{ad}</button>
      ))}
    </div>
  );
}

const DILLER = [['tr', 'TR'], ['en', 'EN']] as const;
/** Tek DİL anahtarı: iki dilli alanlar seçili dilde gösterilir. */
export function DilAnahtari({ dil, degis }: { dil: Dil; degis: (d: Dil) => void }) {
  return <Secmeli etiket="Düzenlenen dil" secenekler={DILLER} deger={dil} degis={degis} />;
}

/* ─── Alanlar ──────────────────────────────────────────────────────────── */
export function Alan({ id, etiket, ipucu, hata, sayac, children }: {
  id?: string; etiket: React.ReactNode; ipucu?: React.ReactNode; hata?: string | null; sayac?: string; children: React.ReactNode;
}) {
  return (
    <div className="ccd-field">
      <div className="ccd-site-etiket">
        {id ? <label className="ccd-field-label" htmlFor={id}>{etiket}</label> : <span className="ccd-field-label">{etiket}</span>}
        {sayac && <span className="ccd-site-sayac">{sayac}</span>}
      </div>
      {children}
      {hata ? <div className="ccd-field-error" role="alert">{hata}</div> : ipucu ? <div className="ccd-field-hint">{ipucu}</div> : null}
    </div>
  );
}

/** İki dilli alanın SEÇİLİ dildeki kutusu. EN boşsa Türkçesi yer tutucu olur ve not düşülür. */
export function DilAlan({ id, etiket, deger, degis, dil, azami, cokSatir, satir = 3, ipucu, sayac }: {
  id: string; etiket: string; deger: IkiDil; degis: (v: IkiDil) => void; dil: Dil; azami?: number; cokSatir?: boolean; satir?: number;
  ipucu?: string; sayac?: boolean;
}) {
  const ortak = {
    id, value: deger[dil], maxLength: azami, lang: dil,
    placeholder: dil === 'en' ? deger.tr : undefined,
  };
  const enBos = dil === 'en' && !deger.en.trim();
  return (
    <Alan id={id} etiket={<>{etiket}<span className="ccd-site-dil-cip">{dil.toUpperCase()}</span></>}
          sayac={sayac && azami ? `${deger[dil].length} / ${azami}` : undefined}
          ipucu={enBos ? (deger.tr ? 'Boş — sitede Türkçesi kullanılır.' : ipucu) : ipucu}>
      {cokSatir
        ? <textarea className="ccd-textarea" rows={satir} {...ortak} onChange={(e) => degis({ ...deger, [dil]: e.target.value })} />
        : <input className="ccd-input" {...ortak} onChange={(e) => degis({ ...deger, [dil]: e.target.value })} />}
    </Alan>
  );
}

/** Site içi yol ya da https bağlantısı. */
export function BaglantiAlan({ id, etiket = 'Bağlantı', deger, degis, ipucu }: { id: string; etiket?: string; deger: string; degis: (v: string) => void; ipucu?: string }) {
  return (
    <Alan id={id} etiket={etiket} ipucu={ipucu}>
      <input id={id} className="ccd-input ccd-mono" maxLength={300} placeholder="/urunler ya da https://…" value={deger} onChange={(e) => degis(e.target.value)} />
    </Alan>
  );
}

/** Açık/kapalı anahtarı (etiketiyle). */
export function Anahtar({ acik, degis, etiket }: { acik: boolean; degis: (v: boolean) => void; etiket: string }) {
  return (
    <label className="ccd-site-anahtar">
      <button type="button" role="switch" aria-checked={acik} aria-label={etiket} className={`ccd-switch${acik ? ' ccd-is-on' : ''}`} onClick={() => degis(!acik)} />
      <span>{etiket}</span>
    </label>
  );
}

/* ─── Kaydedilmemiş değişiklikler ──────────────────────────────────────── */
const CIKIS_SORUSU = 'Kaydedilmemiş değişiklikler var. Kaydetmeden çıkılsın mı?';

/**
 * Kaydedilmemiş değişiklik varken sayfadan çıkışı sorar: sekme kapatma/yenileme
 * (tarayıcı uyarısı) ve panel içi bağlantılar. Düğmeyle çıkışlar `cikabilir()` sorar.
 */
export function useCikisUyarisi(kirli: boolean): () => boolean {
  const ref = useRef(kirli);
  ref.current = kirli;
  useEffect(() => {
    if (!kirli) return;
    const kapanis = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    // Yakalama aşamasında: yönlendirici tıklamayı görmeden önce sorulur.
    const tik = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.origin !== window.location.origin || a.pathname === window.location.pathname) return;
      if (!window.confirm(CIKIS_SORUSU)) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('beforeunload', kapanis);
    document.addEventListener('click', tik, true);
    return () => { window.removeEventListener('beforeunload', kapanis); document.removeEventListener('click', tik, true); };
  }, [kirli]);
  return useCallback(() => !ref.current || window.confirm(CIKIS_SORUSU), []);
}

/** Shopify'ın kaydetme çubuğu: bir alan değişince belirir. Kaydet = taslak. */
export function KaydetCubugu({ acik, mesgul, hata, kaydet, vazgec }: {
  acik: boolean; mesgul: boolean; hata?: string | null; kaydet: () => void; vazgec: () => void;
}) {
  if (!acik && !hata) return null;
  return (
    <div className="ccd-site-kaydet" role="region" aria-label="Kaydedilmemiş değişiklikler" data-site-kaydet>
      <span className="ccd-site-kaydet-metin ccd-truncate">{hata ?? 'Kaydedilmemiş değişiklikler'}</span>
      <div className="ccd-row" style={{ gap: 8 }}>
        <button type="button" className="ccd-btn ccd-btn-sm ccd-site-kaydet-vazgec" disabled={mesgul || !acik} onClick={vazgec}>Vazgeç</button>
        <button type="button" className="ccd-btn ccd-btn-sm ccd-site-kaydet-dugme" disabled={mesgul || !acik} onClick={kaydet}>{mesgul ? 'Kaydediliyor…' : 'Kaydet'}</button>
      </div>
    </div>
  );
}

/** Nesne taslağı + son kaydedilen hâli; `kirli` ikisinin farkıdır. */
export function useTaslak<T>() {
  const [f, setF] = useState<T | null>(null);
  const [kayitli, setKayitli] = useState<T | null>(null);
  const yukle = useCallback((v: T) => { setF(v); setKayitli(v); }, []);
  const kirli = f !== null && kayitli !== null && JSON.stringify(f) !== JSON.stringify(kayitli);
  return { f, setF, kayitli, yukle, kirli, vazgec: () => setF(kayitli) };
}

/* ─── Yayın ────────────────────────────────────────────────────────────── */
/** Değişiklikleri bölüme göre gruplayıp listeler. */
export function DegisiklikListesi({ ozet }: { ozet: Degisiklik[] }) {
  const bolumler = [...new Set(ozet.map((d) => d.bolum))];
  return (
    <div className="ccd-stack-sm">
      {bolumler.map((b) => (
        <div key={b}>
          <div className="ccd-site-altbaslik">{b}</div>
          <ul className="ccd-site-degisiklik">
            {ozet.filter((d) => d.bolum === b).map((d, i) => (
              <li key={i}>
                <b>{d.alan}</b>
                {d.once !== undefined && d.sonra !== undefined
                  ? <> : <span className="ccd-site-once">{d.once || '(boş)'}</span> → <span className="ccd-site-sonra">{d.sonra || '(boş)'}</span></>
                  : d.sonra ? <> — {d.sonra}</> : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

type YayinKaydi = { id: number; ok: boolean | null; cikti: string; sureMs: number | null; sayfaSayisi: number | null };

/**
 * "Yayınla" onay penceresi — her ekrandan aynı pencere açılır: ne değişti listesi,
 * Yayınla / Vazgeç, sürerken durum, bitince "Siteyi aç".
 */
export function YayinPenceresi({ kapat }: { kapat: () => void }) {
  const [durum, setDurum] = useState<SiteDurum | null>(null);
  const [asama, setAsama] = useState<'onay' | 'suruyor' | 'tamam' | 'hata'>('onay');
  const [hata, setHata] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<YayinKaydi | null>(null);
  const isId = useRef<number | null>(null);

  useEffect(() => { api<SiteDurum>('/api/site/durum').then(setDurum).catch((e) => setHata((e as Error).message)); }, []);
  useEffect(() => {
    if (asama === 'suruyor') return;
    const tus = (e: KeyboardEvent) => { if (e.key === 'Escape') kapat(); };
    document.addEventListener('keydown', tus);
    return () => document.removeEventListener('keydown', tus);
  }, [asama, kapat]);

  // Yayın arka planda çalışır; bitene kadar durum yoklanır, sonra kaydın sonucu okunur.
  useEffect(() => {
    if (asama !== 'suruyor') return;
    let iptal = false;
    const t = window.setInterval(async () => {
      const d = await api<SiteDurum>('/api/site/durum').catch(() => null);
      if (iptal || !d || d.calisan) return;
      window.clearInterval(t);
      const g = await api<{ gecmis: YayinKaydi[] }>('/api/site/yayin').catch(() => null);
      if (iptal) return;
      const k = g?.gecmis.find((x) => x.id === isId.current) ?? null;
      setDurum(d); setSonuc(k);
      setAsama(k?.ok ? 'tamam' : 'hata');
      if (!k?.ok) setHata('Yayın başarısız oldu — canlı site değişmedi.');
    }, 1500);
    return () => { iptal = true; window.clearInterval(t); };
  }, [asama]);

  async function yayinla() {
    setHata(null);
    try {
      const r = await api<{ id: number }>('/api/site/yayin', { method: 'POST', body: JSON.stringify({ kuru: false }) });
      isId.current = r.id; setAsama('suruyor');
    } catch (e) { setHata(e instanceof Error ? e.message : 'Yayın başlatılamadı'); }
  }

  const degisiklik = durum?.degisiklik;
  const engel = !durum ? 'Yükleniyor…' : !durum.hatKurulu ? 'Site yayın hattı bu sunucuda kurulmamış.' : durum.calisan && asama === 'onay' ? `${IS_AD[durum.calisan.tur]} sürüyor — bitmesini bekleyin.` : null;
  return (
    <div className="ccd-site-perde" onMouseDown={(e) => { if (e.target === e.currentTarget && asama !== 'suruyor') kapat(); }}>
      <div className="ccd-site-pencere ccd-site-pencere-dar" role="dialog" aria-modal="true" aria-label="Siteyi yayınla" data-site-yayin={asama}>
        <div className="ccd-site-pencere-bas">
          <h2 className="ccd-site-kart-baslik">{asama === 'tamam' ? 'Yayınlandı' : asama === 'suruyor' ? 'Yayınlanıyor…' : 'Siteyi yayınla'}</h2>
          {durum?.siteAdresi && <span className="ccd-meta ccd-truncate">{durum.siteAdresi.replace(/^https?:\/\//, '')}</span>}
        </div>

        <div className="ccd-site-pencere-govde ccd-scroll">
          {asama === 'onay' && (
            !durum ? (!hata && <div className="ccd-inline-note">Yükleniyor…</div>)
              : !degisiklik?.var ? <div className="ccd-meta">Yayınlanacak değişiklik yok — taslak ile yayındaki site aynı.</div>
              : (
                <div className="ccd-stack-sm">
                  <div className="ccd-meta">Bu değişiklikler canlı siteye çıkacak:</div>
                  <DegisiklikListesi ozet={degisiklik.ozet} />
                </div>
              )
          )}
          {asama === 'suruyor' && (
            <div className="ccd-stack-sm">
              <div className="ccd-tema-ilerleme" aria-hidden="true" />
              <div className="ccd-meta">Site derleniyor ve canlıya yazılıyor. Bu pencereyi kapatabilirsiniz; yayın arka planda sürer.</div>
            </div>
          )}
          {asama === 'tamam' && (
            <div className="ccd-meta">Değişiklikler canlıda{sonuc?.sayfaSayisi ? ` · ${sonuc.sayfaSayisi} sayfa` : ''}{sonuc?.sureMs ? ` · ${(sonuc.sureMs / 1000).toFixed(1)} sn` : ''}.</div>
          )}
          {asama === 'hata' && sonuc?.cikti && <pre className="ccd-site-cikti ccd-scroll">{sonuc.cikti.slice(-1200)}</pre>}
          {(hata || (asama === 'onay' && engel && durum)) && <div className="ccd-site-not ccd-site-not-hata" role="alert">{hata ?? engel}</div>}
        </div>

        <div className="ccd-site-pencere-alt">
          {asama === 'onay' && (
            <>
              <button type="button" className="ccd-btn" onClick={kapat}>Vazgeç</button>
              <button type="button" className="ccd-btn ccd-btn-primary" disabled={Boolean(engel) || !degisiklik?.var} onClick={() => void yayinla()}>Yayınla</button>
            </>
          )}
          {asama !== 'onay' && (
            <>
              {asama === 'hata' && <Link className="ccd-btn" to="/site/yayin-gecmisi" onClick={kapat}>Yayın geçmişi</Link>}
              {asama === 'tamam' && durum?.siteAdresi && <a className="ccd-btn" href={durum.siteAdresi} target="_blank" rel="noreferrer">Siteyi aç</a>}
              <button type="button" className="ccd-btn ccd-btn-primary" onClick={kapat}>{asama === 'suruyor' ? 'Arka planda sürdür' : 'Kapat'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Yayınla" düğmesi + penceresi. `engel`: düğmeyi kapatan neden (örn. kaydedilmemiş değişiklik). */
export function YayinDugmesi({ durum, yenile, engel, birincil = true, kucuk }: {
  durum: SiteDurum | null; yenile: () => void; engel?: string | null; birincil?: boolean; kucuk?: boolean;
}) {
  const [acik, setAcik] = useState(false);
  const kapat = useCallback(() => { setAcik(false); yenile(); }, [yenile]);
  const neden = engel ?? (durum && !durum.hatKurulu ? 'Site yayın hattı bu sunucuda kurulmamış' : durum?.calisan ? 'Bir iş sürüyor — bitmesini bekleyin' : null);
  return (
    <>
      <button type="button" className={`ccd-btn${birincil ? ' ccd-btn-primary' : ''}${kucuk ? ' ccd-btn-sm' : ''}`} data-site-yayinla
              disabled={!durum || Boolean(neden)} title={neden ?? undefined} onClick={() => setAcik(true)}>Yayınla</button>
      {acik && <YayinPenceresi kapat={kapat} />}
    </>
  );
}

/** Son yayının tek satırlık özeti. */
export function sonYayinMetni(d: SiteDurum): string {
  if (!d.sonYayin) return 'Panelden henüz yayın yapılmadı';
  return `Son yayın ${d.sonYayin.bitis ? formatTarih(d.sonYayin.bitis) : '—'}${d.sonYayin.kullanici ? ` · ${d.sonYayin.kullanici}` : ''}`;
}

/* ─── Görsel gösterimi ─────────────────────────────────────────────────── */
const blobOnbellek = new Map<string, Promise<string>>();

/**
 * Bir site yolunun (/img/…) tarayıcıda gösterilebilir adresi.
 *  · /img/panel/<ad> → kütüphane görseli; jetonla çekilip blob adresine çevrilir
 *  · diğer /img/…    → sitede zaten duran görsel; sitenin kendi adresinden gösterilir
 */
export function gorselAdresi(yol: string): Promise<string> {
  const m = /^\/img\/panel\/([a-f0-9]{32}\.(?:jpg|png|webp))$/.exec(yol);
  if (!m) return adresBekle.then(() => (yol.startsWith('/img/') && siteAdresi ? `${siteAdresi.replace(/\/$/, '')}${yol}` : ''));
  let p = blobOnbellek.get(m[1]);
  if (!p) {
    p = yetkiliFetch(`/api/site/medya/dosya/${m[1]}`).then((r) => (r.ok ? r.blob() : Promise.reject(new Error('yok')))).then((b) => URL.createObjectURL(b));
    p.catch(() => blobOnbellek.delete(m[1]));
    blobOnbellek.set(m[1], p);
  }
  return p;
}

export function SiteGorsel({ yol, alt = '', className }: { yol: string; alt?: string; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [yok, setYok] = useState(false);
  useEffect(() => {
    let iptal = false;
    setSrc(null); setYok(false);
    if (!yol) return;
    gorselAdresi(yol).then((a) => { if (!iptal) { if (a) setSrc(a); else setYok(true); } }).catch(() => { if (!iptal) setYok(true); });
    return () => { iptal = true; };
  }, [yol]);
  if (!yol) return <div className={`ccd-site-gorsel-bos ${className ?? ''}`}>görsel yok</div>;
  if (yok) return <div className={`ccd-site-gorsel-bos ${className ?? ''}`} title={yol}>{yol.startsWith('/img/panel/') ? 'görsel bulunamadı' : 'sitedeki görsel'}</div>;
  if (!src) return <div className={`ccd-site-gorsel-bos ${className ?? ''}`}>…</div>;
  return <img className={className} src={src} alt={alt} loading="lazy" onError={() => setYok(true)} />;
}

/* ─── Kütüphaneye yükleme ──────────────────────────────────────────────── */
/** Dosyaları sırayla yükler (sunucu istek başına tek dosya alır). Eklenenleri döner; ilk hatada durur. */
export async function medyaYukle(dosyalar: File[], ilerleme?: (sira: number, toplam: number) => void): Promise<Medya[]> {
  const eklenen: Medya[] = [];
  for (let n = 0; n < dosyalar.length; n++) {
    ilerleme?.(n + 1, dosyalar.length);
    const f = dosyalar[n];
    const fd = new FormData();
    // Panodan yapıştırılan görselin adı olmayabilir; uzantı türden türetilir.
    fd.append('dosya', f, f.name || `gorsel.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`);
    const res = await yetkiliFetch('/api/site/medya', { method: 'POST', body: fd });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(`${f.name || 'Görsel'}: ${j.error ?? 'yüklenemedi'}`);
    eklenen.push(j.gorsel as Medya);
  }
  return eklenen;
}

/** Gözat + sürükle-bırak + Ctrl+V için ortak bağlayıcı. */
export function useYuklemeAlani(yukle: (dosyalar: File[]) => void, etkin = true) {
  const [uzerinde, setUzerinde] = useState(false);
  const ref = useRef(yukle);
  ref.current = yukle;
  // Sayfada Ctrl+V: panodaki görsel yüklenir (yazı alanına yapıştırma hariç).
  useEffect(() => {
    if (!etkin) return;
    const yapistir = (e: ClipboardEvent) => {
      const hedef = e.target as HTMLElement | null;
      if (hedef && (hedef.tagName === 'INPUT' || hedef.tagName === 'TEXTAREA' || hedef.isContentEditable)) return;
      const fs = [...(e.clipboardData?.files ?? [])].filter((x) => x.type.startsWith('image/'));
      if (fs.length) { e.preventDefault(); ref.current(fs); }
    };
    document.addEventListener('paste', yapistir);
    return () => document.removeEventListener('paste', yapistir);
  }, [etkin]);
  const alan = {
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); setUzerinde(true); },
    onDragLeave: () => setUzerinde(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault(); setUzerinde(false);
      ref.current([...e.dataTransfer.files].filter((x) => x.type.startsWith('image/')));
    },
  };
  return { uzerinde, alan };
}

/* ─── Görsel seçici (kütüphaneden) ─────────────────────────────────────── */
export function GorselSecici({ secili, sec, kapat }: { secili?: string; sec: (yol: string, medya: Medya) => void; kapat: () => void }) {
  const [liste, setListe] = useState<Medya[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [ara, setAra] = useState('');
  const getir = useCallback(() => api<{ gorseller: Medya[] }>('/api/site/medya').then((r) => setListe(r.gorseller)).catch((e) => setHata((e as Error).message)), []);
  useEffect(() => { void getir(); }, [getir]);
  useEffect(() => {
    const tus = (e: KeyboardEvent) => { if (e.key === 'Escape') kapat(); };
    document.addEventListener('keydown', tus);
    return () => document.removeEventListener('keydown', tus);
  }, [kapat]);

  const yukle = useCallback(async (dosyalar: File[]) => {
    if (!dosyalar.length) return;
    setHata(null);
    try {
      const eklenen = await medyaYukle(dosyalar, (s, t) => setMesgul(`${s} / ${t} yükleniyor…`));
      // Tek görsel yüklendiyse doğrudan seçilir — kullanıcı zaten onu istiyordu.
      if (eklenen.length === 1) { sec(eklenen[0].yol, eklenen[0]); return; }
    } catch (e) { setHata(e instanceof Error ? e.message : 'Görsel yüklenemedi'); }
    finally { setMesgul(null); await getir(); }
  }, [getir, sec]);
  const { uzerinde, alan } = useYuklemeAlani((fs) => void yukle(fs));

  const q = ara.trim().toLocaleLowerCase('tr');
  const gorunen = (liste ?? []).filter((m) => !q || `${m.dosyaAdi} ${m.alt}`.toLocaleLowerCase('tr').includes(q));

  return (
    <div className="ccd-site-perde" onMouseDown={(e) => { if (e.target === e.currentTarget) kapat(); }}>
      <div className={`ccd-site-pencere ccd-stack${uzerinde ? ' ccd-site-uzerinde' : ''}`} role="dialog" aria-modal="true" aria-label="Görsel seç" {...alan}>
        <div className="ccd-row-between">
          <div className="ccd-fieldset-title" style={{ margin: 0 }}>Görsel seç</div>
          <div className="ccd-row" style={{ gap: 8 }}>
            <label className="ccd-btn ccd-btn-sm" style={{ cursor: mesgul ? 'default' : 'pointer' }}>
              {mesgul ?? 'Yeni görsel yükle'}
              <input type="file" accept={GORSEL_KABUL} multiple hidden disabled={!!mesgul}
                     onChange={(e) => { const fs = [...(e.target.files ?? [])]; e.target.value = ''; void yukle(fs); }} />
            </label>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
          </div>
        </div>
        {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}
        {(liste?.length ?? 0) > 12 && (
          <input className="ccd-input" placeholder="Dosya adı ya da alternatif metinde ara…" aria-label="Görsellerde ara" value={ara} onChange={(e) => setAra(e.target.value)} />
        )}
        {!liste ? <div className="ccd-inline-note">Yükleniyor…</div> : liste.length === 0 ? (
          <div className="ccd-empty">Kütüphane boş. "Yeni görsel yükle" ile seçin, görseli bu pencereye sürükleyin ya da Ctrl+V ile yapıştırın.</div>
        ) : (
          <div className="ccd-site-izgara ccd-scroll">
            {gorunen.map((m) => (
              <button type="button" key={m.id} className={`ccd-site-kare${secili === m.yol ? ' ccd-is-active' : ''}`} title={m.dosyaAdi}
                      onClick={() => sec(m.yol, m)}>
                <SiteGorsel yol={m.yol} alt={m.alt} className="ccd-site-kare-gorsel" />
                <span className="ccd-site-kare-ad ccd-truncate">{m.alt || m.dosyaAdi}</span>
              </button>
            ))}
            {gorunen.length === 0 && <div className="ccd-meta">Aramaya uyan görsel yok.</div>}
          </div>
        )}
        <div className="ccd-field-hint">JPG, PNG ya da WEBP · en çok 8 MB · yüklenen görsel kütüphaneye eklenir, siteye ancak yayınla çıkar.</div>
      </div>
    </div>
  );
}

/** Tek görsel alanı: küçük önizleme + "Değiştir" / "Kaldır". Değer bir site yoludur (/img/…). */
export function GorselAlan({ etiket, deger, degis, ipucu, zorunlu, genis }: {
  etiket: string; deger: string; degis: (yol: string) => void; ipucu?: string; zorunlu?: boolean;
  /** Kapak gibi tek başına duran görsel: kart genişliğinde önizleme. */
  genis?: boolean;
}) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="ccd-field">
      {etiket && <span className="ccd-field-label">{etiket}{zorunlu && <span className="ccd-required"> *</span>}</span>}
      <div className={`ccd-site-gorsel-alan${genis ? ' ccd-site-gorsel-genis' : ''}`}>
        <SiteGorsel yol={deger} className="ccd-site-gorsel-onizleme" />
        <div className="ccd-site-gorsel-eylem">
          <button type="button" className="ccd-btn ccd-btn-sm" onClick={() => setAcik(true)}>{deger ? 'Değiştir' : 'Görsel seç'}</button>
          {deger && !zorunlu && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => degis('')}>Kaldır</button>}
        </div>
      </div>
      {ipucu && <div className="ccd-field-hint">{ipucu}</div>}
      {acik && <GorselSecici secili={deger} sec={(yol) => { degis(yol); setAcik(false); }} kapat={() => setAcik(false)} />}
    </div>
  );
}

/** Görsel listesi (hero kareleri, galeri): küçük kareler + "ekle". */
export function GorselListesi({ etiket, deger, degis, azami, bos }: { etiket: string; deger: string[]; degis: (v: string[]) => void; azami: number; bos: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="ccd-field">
      <div className="ccd-site-etiket">
        <span className="ccd-field-label">{etiket}</span>
        <span className="ccd-site-sayac">{deger.length} / {azami}</span>
      </div>
      <div className="ccd-site-serit">
        {deger.map((g, i) => (
          <div className="ccd-site-kucuk" key={g + i}>
            <SiteGorsel yol={g} className="ccd-site-kucuk-gorsel" />
            <button type="button" className="ccd-site-kucuk-sil" aria-label="Görseli kaldır" title="Kaldır" onClick={() => degis(deger.filter((_, n) => n !== i))}>×</button>
          </div>
        ))}
        {deger.length < azami && <button type="button" className="ccd-site-kucuk-ekle" onClick={() => setAcik(true)}>+ Ekle</button>}
      </div>
      {deger.length === 0 && <div className="ccd-field-hint">{bos}</div>}
      {acik && <GorselSecici sec={(yol) => { degis(deger.includes(yol) ? deger : [...deger, yol].slice(0, azami)); setAcik(false); }} kapat={() => setAcik(false)} />}
    </div>
  );
}
